import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { PlusIcon, CheckCircleIcon, XMarkIcon, CameraIcon } from '@heroicons/react/24/outline'
import { weeklyProgramsAPI } from '../../services/api'
import { trainingLocationsAPI } from '../../services/trainingLocations'
import { ExerciseCard } from './ExerciseCard'
import { SubstituteExerciseModal } from './SubstituteExerciseModal'
import { SessionLocationControl } from '../capture/SessionLocationControl'
import WorkoutPhotoUpload from './WorkoutPhotoUpload'
import WorkoutPhotoReview from './WorkoutPhotoReview'
import { useOptionalAuth } from '../../hooks/useAuth'
import { isSetReported } from '../../utils/captureProgress'

// UAT 2026-09-15: substituting deletes these — the pickers confirm first.
const reportedSetCount = (exercise) =>
  (exercise?.set_results || []).filter(isSetReported).length

export function InlineSessionContent({ session, program, queryClient }) {
  const { t, i18n } = useTranslation()
  const [substitutingExercise, setSubstitutingExercise] = useState(null)
  // VH-207: a remembered substitute is a program change — signed-in only.
  const { user } = useOptionalAuth()
  const [sessionData, setSessionData] = useState({
    actual_date: session.actual_date || new Date().toISOString().split('T')[0],
    session_notes: session.session_notes || ''
  })
  const [showPhotoUpload, setShowPhotoUpload] = useState(false)
  const [photoAnalysisResult, setPhotoAnalysisResult] = useState(null)

  const isGerman = i18n.language === 'de'
  const moduleType = session.snapshot_module_type || 'strength'

  // VH-212: this view's picker was UNSCOPED — it passed no location at all,
  // while the capture sheet passed one. Two doors to the same modal behaving
  // differently is how a user learns to distrust the list. Scope it to where
  // the session actually is.
  //
  // `resolved_location_id` is the server's session -> module -> none walk;
  // `location_id` alone is only the override and is null for almost every
  // existing row, so relying on it would leave this view unscoped exactly where
  // the defect lives.
  //
  // The fallback to the override is BELT-AND-BRACES, not load-bearing: it
  // covers an endpoint that forgot to populate the resolved fields, which fails
  // silently (under-scoped, still rendering, no error) rather than loudly. That
  // was a real hazard — three such routes existed and were fixed — and the
  // backend now has a structural test that walks the router and refuses a route
  // returning a session payload without a `_finalize_*` call. Keep this anyway:
  // it costs one `??` and turns a future regression into a shorter list rather
  // than a wrong one. The name is looked up locally when the payload has
  // not carried it: scoping this list SHORTENS something users see today, and a
  // shorter list with no stated reason reads as missing data — the same
  // invisibility that caused this feature.
  const { data: locationsData } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })
  const sessionLocationId = session.resolved_location_id ?? session.location_id ?? null
  const sessionLocationName =
    session.resolved_location_name ??
    (locationsData?.locations || []).find((l) => l.id === sessionLocationId)?.name ??
    null

  const updateSessionMutation = useMutation({
    // VH-124 Fix 4: `__silent` is a client-only flag (stripped before the request)
    // that suppresses the "Session saved" toast when this save is piggy-backed on
    // a per-exercise save (ExerciseCard already toasts "results saved") — one user
    // Save must show exactly one toast.
    mutationFn: ({ __silent, ...data }) =>
      weeklyProgramsAPI.updateSession(program.id, session.id, data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      if (!variables?.__silent) {
        toast.success(t('programs.sessionSaved') || 'Session saved')
      }
    }
  })

  // VH-118: manually toggle session completion via the dedicated endpoints
  // (separate from the auto-complete rollup). Invalidate so the card reflects
  // the new state without a reload; a SUBMITTED program returns 409.
  const toggleCompleteMutation = useMutation({
    mutationFn: (complete) =>
      complete
        ? weeklyProgramsAPI.markSessionComplete(session.id)
        : weeklyProgramsAPI.markSessionIncomplete(session.id),
    onSuccess: (_res, complete) => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      // VH-124 Fix 3: on completion, confirm success and let the view collapse to
      // its read-only/locked state (is_completed → isEditable=false hides the
      // capture form). Mark-incomplete keeps its plain confirmation.
      toast.success(
        complete
          ? t('capture.completedSuccess', 'Session successfully captured and marked as completed')
          : t('programs.markedIncomplete', 'Session marked incomplete')
      )
    },
    onError: (error) => {
      if (error?.response?.status === 409) {
        toast.error(t('capture.finalizedError'))
      } else {
        toast.error(t('common.error', 'Something went wrong'))
      }
    }
  })

  const substituteExerciseMutation = useMutation({
    mutationFn: ({ exerciseId, newExerciseId, reason }) =>
      weeklyProgramsAPI.substituteExercise(exerciseId, { new_exercise_id: newExerciseId, reason }),
    onSuccess: async () => {
      setSubstitutingExercise(null)
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      await queryClient.refetchQueries({ queryKey: ['programDetail', program.id] })
      toast.success(t('programs.exerciseSubstituted'))
    },
    onError: (error) => {
      console.error('Failed to substitute exercise:', error)
      toast.error(t('programs.exerciseSubstituteFailed'))
    }
  })

  const addExerciseMutation = useMutation({
    mutationFn: (data) => weeklyProgramsAPI.addExercise(session.id, data),
    onSuccess: async () => {
      setSubstitutingExercise(null)
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      await queryClient.refetchQueries({ queryKey: ['programDetail', program.id] })
      toast.success(t('programs.exerciseAdded', 'Exercise added'))
    },
    onError: (error) => {
      console.error('Failed to add exercise:', error)
      toast.error(t('programs.exerciseAddFailed', 'Failed to add exercise'))
    }
  })

  const handleSessionDataChange = (field, value) => {
    setSessionData({ ...sessionData, [field]: value })
  }

  // VH-124 Fix 4: `options.silent` suppresses the session-save toast so a
  // per-exercise Save (which also persists the session date/notes) shows only
  // the exercise's own "results saved" toast. The standalone Save Draft button
  // calls this with no options → toast still shows.
  const handleSaveSessionData = (options = {}) => {
    updateSessionMutation.mutate({
      actual_date: sessionData.actual_date || null,
      session_notes: sessionData.session_notes || null,
      __silent: options?.silent === true
    })
  }

  const handleSubstituteConfirm = (newExercise) => {
    if (!substitutingExercise || !newExercise) return

    if (substitutingExercise.isNewActivity) {
      addExerciseMutation.mutate({
        snapshot_exercise_name_en: newExercise.name_en,
        snapshot_exercise_type: newExercise.exercise_type || 'strength',
        snapshot_muscle_group: newExercise.muscle_group || null,
        snapshot_difficulty: newExercise.difficulty_level || null,
        source_exercise_id: newExercise.id,
        source_equipment_id: newExercise.equipment_id || null,
        snapshot_equipment_name_en: newExercise.equipment?.name_en || null,
        warmup_sets: newExercise.default_warmup_sets || 0,
        working_sets: newExercise.default_training_sets || 3,
        rest_seconds: newExercise.default_rest_seconds || 60,
      })
    } else {
      substituteExerciseMutation.mutate({
        exerciseId: substitutingExercise.id,
        newExerciseId: newExercise.id,
        reason: 'Equipment unavailable'
      })
    }
  }

  const applyPhotoResultsMutation = useMutation({
    mutationFn: (exercises) => weeklyProgramsAPI.applyPhotoResults(session.id, {
      analysis_id: photoAnalysisResult?.analysis_id,
      exercises
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      setShowPhotoUpload(false)
      setPhotoAnalysisResult(null)
      toast.success(t('workouts.photo.resultsApplied', 'Photo results applied successfully'))
    },
    onError: (error) => {
      console.error('Failed to apply photo results:', error)
      toast.error(t('workouts.photo.applyFailed', 'Failed to apply photo results'))
    }
  })

  const handleApplyPhotoResults = (editedExercises) => {
    applyPhotoResultsMutation.mutate(editedExercises)
  }

  const handleClosePhotoModal = () => {
    setShowPhotoUpload(false)
    setPhotoAnalysisResult(null)
  }

  const isEditable = program.status !== 'submitted' && !session?.is_completed

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      {/* VH-124 Fix 3: completed sessions are locked & read-only. The reopen
          affordance is removed here — the VH-118 toggle below is the single
          completion control (owner-only correction path). */}
      {!isEditable && session?.is_completed && program.status !== 'submitted' && (
        <div className="sticky top-0 z-20 bg-green-50 border-b-2 border-green-200 p-3 shadow-md">
          <span className="text-sm text-green-700 font-medium flex items-center">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {t('capture.completedAndLocked')}
          </span>
        </div>
      )}
      {isEditable && (
        <div className="sticky top-0 z-20 bg-indigo-50 border-b-2 border-indigo-200 p-3 shadow-md">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSubstitutingExercise({ id: null, isNewActivity: true })}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-surface-container border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center shadow-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                {t('programs.addActivity', 'Add Exercise')}
              </button>
              <button
                onClick={() => setShowPhotoUpload(true)}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-surface-container border border-indigo-300 text-indigo-600 hover:bg-indigo-50 flex items-center shadow-sm"
              >
                <CameraIcon className="h-4 w-4 mr-1.5" />
                {t('workouts.photo.scanResults', 'Scan Results')}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveSessionData()}
                disabled={updateSessionMutation.isPending}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-surface-container border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
              >
                {updateSessionMutation.isPending ? t('common.saving') : t('programs.saveDraft')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="p-4 space-y-4">

        {/* VH-212: the program view scopes its picker by the session's
            location, so it must also offer the way to change it. Shipping the
            scope without the control leaves a filter the athlete can see and
            cannot move — QA raised exactly that. Same control and same
            authenticated PUT as the capture sheet, mounted at the second door;
            it needs the session's intake_token, and hides itself without one. */}
        {session.intake_token && isEditable && (
          <SessionLocationControl
            token={session.intake_token}
            locationId={sessionLocationId}
            locationName={sessionLocationName}
            isOverride={Boolean(session.location_is_override)}
            onChanged={() =>
              queryClient.refetchQueries({ queryKey: ['programDetail', program.id] })
            }
          />
        )}

        {/* VH-118: manual complete / incomplete toggle (dedicated endpoints). */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => toggleCompleteMutation.mutate(!session.is_completed)}
            disabled={toggleCompleteMutation.isPending}
            className={`px-3 py-2 text-sm font-medium rounded-lg border flex items-center shadow-sm disabled:opacity-60 ${
              session.is_completed
                ? 'bg-surface-container border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-green-600 border-green-600 text-on-neutral hover:bg-green-700'
            }`}
          >
            <CheckCircleIcon className="h-4 w-4 mr-1.5" />
            {toggleCompleteMutation.isPending
              ? t('common.saving')
              : session.is_completed
              ? t('programs.markIncomplete')
              : t('programs.markComplete')}
          </button>
        </div>

        {isEditable && (
          <div className="bg-surface-container rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('trainingDays.dateCompleted')}
                </label>
                <input
                  type="date"
                  value={sessionData.actual_date}
                  onChange={(e) => handleSessionDataChange('actual_date', e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('programs.sessionNotes')}
                </label>
                <input
                  type="text"
                  value={sessionData.session_notes}
                  onChange={(e) => handleSessionDataChange('session_notes', e.target.value)}
                  className="input w-full"
                  placeholder={isGerman ? 'z.B. Erstes Training nach Krankheit' : 'e.g. First training after being sick'}
                />
              </div>
            </div>
          </div>
        )}

        {(program.status === 'submitted' || session.is_completed) && (session.actual_date || session.session_notes) && (
          <div className="bg-surface-container rounded-lg p-4 border border-gray-200">
            {session.actual_date && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{t('programs.performedOn')}:</span> {new Date(session.actual_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
              </div>
            )}
            {session.session_notes && (
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{t('programs.sessionNotes')}:</span> {session.session_notes}
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {session.exercises && session.exercises.length > 0 ? (
            session.exercises
              .sort((a, b) => a.exercise_order - b.exercise_order)
              .map((exercise) => (
                <div key={exercise.id} className="space-y-2">
                {/* VH-207: the location's remembered substitute is OFFERED,
                    never applied — taking it is the same substitute POST as
                    the picker, exactly as on the capture sheet. */}
                {user && isEditable && !exercise.is_substitute && !exercise.is_skipped &&
                  exercise.variant?.suggested_substitute_exercise_id && (
                  <div
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm"
                    data-testid={`inline-variant-substitute-${exercise.id}`}
                  >
                    <span className="flex-1 min-w-0 text-gray-700">
                      {t('variants.offerSubstitute', {
                        name: exercise.variant.suggested_substitute_exercise_name,
                      })}
                    </span>
                    <button
                      type="button"
                      disabled={substituteExerciseMutation.isPending}
                      onClick={() => {
                        const reported = reportedSetCount(exercise)
                        // Substituting deletes reported sets — never silently.
                        if (reported > 0 && !window.confirm(t('programs.substituteDeletesReported', {
                          count: reported, name: exercise.snapshot_exercise_name_en,
                        }))) return
                        substituteExerciseMutation.mutate({
                          exerciseId: exercise.id,
                          newExerciseId: exercise.variant.suggested_substitute_exercise_id,
                        })
                      }}
                      data-testid={`inline-variant-substitute-use-${exercise.id}`}
                      className="rounded-lg bg-primary-600 px-3 py-1 text-xs font-medium text-on-primary hover:bg-primary-700 disabled:opacity-50"
                    >
                      {t('variants.useSubstitute', {
                        name: exercise.variant.suggested_substitute_exercise_name,
                      })}
                    </button>
                  </div>
                )}
                <ExerciseCard
                  exercise={exercise}
                  program={program}
                  session={session}
                  moduleType={moduleType}
                  sessionData={sessionData}
                  onSaveSession={handleSaveSessionData}
                  onSubstitute={() => setSubstitutingExercise(exercise)}
                />
                </div>
              ))
          ) : (
            <div className="text-center py-6 text-gray-500 bg-surface-container rounded-lg border border-gray-200">
              {t('templates.noExercises')}
            </div>
          )}
        </div>
      </div>

      {substitutingExercise && (
        <SubstituteExerciseModal
          exercise={substitutingExercise}
          locationId={sessionLocationId || undefined}
          locationName={sessionLocationName || undefined}
          onConfirm={handleSubstituteConfirm}
          onClose={() => setSubstitutingExercise(null)}
          reportedSetCount={reportedSetCount(substitutingExercise)}
          isLoading={substituteExerciseMutation.isPending || addExerciseMutation.isPending}
        />
      )}

      {showPhotoUpload && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {photoAnalysisResult
                  ? t('workouts.photo.reviewResults', 'Review Extracted Results')
                  : t('workouts.photo.scanWorkoutSheet', 'Scan Workout Sheet')
                }
              </h2>
              <button onClick={handleClosePhotoModal}>
                <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {!photoAnalysisResult ? (
                <WorkoutPhotoUpload
                  session={session}
                  onAnalysisComplete={setPhotoAnalysisResult}
                  onCancel={() => setShowPhotoUpload(false)}
                />
              ) : (
                <WorkoutPhotoReview
                  analysis={photoAnalysisResult}
                  session={session}
                  onApply={handleApplyPhotoResults}
                  onCancel={() => setPhotoAnalysisResult(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
